type ServiceWorkerRegistrationLike = {
  unregister: () => Promise<boolean>;
};

type ServiceWorkerContainerLike = {
  getRegistrations: () => Promise<readonly ServiceWorkerRegistrationLike[]>;
};

type CacheStorageLike = {
  keys: () => Promise<string[]>;
  delete: (key: string) => Promise<boolean>;
};

type SessionStorageLike = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem?: (key: string) => void;
};

type LocalStorageLike = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
};

type CacheCleanupRuntime = {
  serviceWorker?: ServiceWorkerContainerLike;
  cacheStorage?: CacheStorageLike;
  sessionStorage?: SessionStorageLike;
  localStorage?: LocalStorageLike;
  reload?: () => void;
  cleanupVersion?: string;
};

const CLEANUP_MARKER = 'stale-client-cache-cleared-version';
const AUTH_CLEANUP_MARKER = 'auth-cleanup-version';
const DEFAULT_CLEANUP_VERSION = typeof __APP_BUILD_ID__ !== 'undefined' ? __APP_BUILD_ID__ : 'dev';

const getDefaultRuntime = (): CacheCleanupRuntime => ({
  serviceWorker: typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined,
  cacheStorage: typeof caches !== 'undefined' ? caches : undefined,
  sessionStorage: typeof window !== 'undefined' ? window.sessionStorage : undefined,
  localStorage: typeof window !== 'undefined' ? window.localStorage : undefined,
  reload: typeof window !== 'undefined' ? () => window.location.reload() : undefined,
});

export const clearStaleClientCaches = async (
  runtime: CacheCleanupRuntime = getDefaultRuntime()
) => {
  let cleanedAnything = false;
  const cleanupVersion = runtime.cleanupVersion ?? DEFAULT_CLEANUP_VERSION;
  const authCleanupVersion = runtime.localStorage?.getItem(AUTH_CLEANUP_MARKER);
  const hasPersistedAuth =
    Boolean(runtime.localStorage?.getItem('token')) || Boolean(runtime.localStorage?.getItem('user'));

  if (runtime.localStorage && authCleanupVersion !== cleanupVersion) {
    runtime.localStorage.setItem(AUTH_CLEANUP_MARKER, cleanupVersion);

    if (hasPersistedAuth) {
      runtime.localStorage.removeItem('user');
      runtime.localStorage.removeItem('token');
      cleanedAnything = true;
    }
  }

  if (runtime.serviceWorker?.getRegistrations) {
    const registrations = await runtime.serviceWorker.getRegistrations();
    if (registrations.length > 0) {
      await Promise.all(registrations.map((registration) => registration.unregister()));
      cleanedAnything = true;
    }
  }

  if (runtime.cacheStorage?.keys) {
    const cacheKeys = await runtime.cacheStorage.keys();
    if (cacheKeys.length > 0) {
      await Promise.all(cacheKeys.map((key) => runtime.cacheStorage!.delete(key)));
      cleanedAnything = true;
    }
  }

  if (
    cleanedAnything &&
    runtime.sessionStorage &&
    runtime.reload &&
    runtime.sessionStorage.getItem(CLEANUP_MARKER) !== cleanupVersion
  ) {
    runtime.sessionStorage.setItem(CLEANUP_MARKER, cleanupVersion);
    runtime.reload();
  }
};
